import React, { useEffect, useState, useRef } from 'react';
import { XIcon, ShoppingCartIcon, TagIcon, CheckIcon, CopyIcon } from 'lucide-react';
type ExitIntentModalProps = {
  /** Fired when the visitor accepts the offer (e.g. continue to checkout). */
  onAccept: () => void;
  /** Optional callback when the modal is shown (e.g. fire a pixel event). */
  onShow?: () => void;
  /** Only arm the exit-intent listeners when true (e.g. after 27 min). */
  enabled?: boolean;
  /** Displayed offer price (defaults to the standard $297 offer). */
  priceLabel?: string;
  /** Exit-incentive discount shown in the coupon block. */
  discountLabel?: string;
  /** Coupon code the visitor enters at checkout to claim the discount. */
  couponCode?: string;
  /** When false, hide the extra-discount coupon block entirely. */
  showDiscount?: boolean;
  /**
   * Preview helper: open the popup immediately on mount (ignores the exit
   * gesture and the once-per-session guard). Used by the ?exitdemo=1 demo link.
   */
  autoOpen?: boolean;
};
const SESSION_KEY = 'vsl_exit_intent_shown';
/**
 * Exit-intent recovery popup. Triggers when the visitor moves the cursor
 * toward the top of the viewport (desktop) or attempts to navigate back
 * (mobile / browser back button). Shows at most once per session.
 */
export function ExitIntentModal({
  onAccept,
  onShow,
  enabled = true,
  priceLabel = '$297',
  discountLabel = '$27 OFF',
  couponCode = '27OFF',
  showDiscount = true,
  autoOpen = false
}: ExitIntentModalProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const firedRef = useRef(false);
  // Preview: pop the modal right away when autoOpen is set (?exitdemo=1).
  useEffect(() => {
    if (autoOpen && !firedRef.current) {
      firedRef.current = true;
      setOpen(true);
      onShow?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);
  const copyCode = () => {
    try {
      navigator.clipboard?.writeText(couponCode);
    } catch {}
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  const trigger = () => {
    if (!enabled) return;
    if (firedRef.current) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {}
    firedRef.current = true;
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {}
    setOpen(true);
    onShow?.();
  };
  useEffect(() => {
    // Only arm the exit-intent listeners once enabled (e.g. after 27 min).
    if (!enabled) return;
    // DESKTOP: cursor leaves through the top edge of the document.
    const onMouseOut = (e: MouseEvent) => {
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };
    document.addEventListener('mouseout', onMouseOut);
    // MOBILE / back button: push a state so the first back press opens the
    // modal instead of leaving the page.
    let pushed = false;
    try {
      window.history.pushState({
        exitGuard: true
      }, '');
      pushed = true;
    } catch {}
    const onPopState = () => trigger();
    window.addEventListener('popstate', onPopState);
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('popstate', onPopState);
      if (pushed) {
        // best-effort cleanup; ignored if already navigated
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!open) return null;
  return <div role="dialog" aria-modal="true" aria-label="Espera, no te vayas" className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6" data-id="element-67" data-mp="33np89ycqzxtf">
      <style data-id="element-68" data-mp="12ky5yfxa8ddr">{`@keyframes exitPop{0%{transform:scale(.9)translateY(8px);opacity:0}60%{transform:scale(1.02)}100%{transform:scale(1)translateY(0);opacity:1}}.exit-pop{animation:exitPop .4s cubic-bezier(.34,1.56,.64,1) both}`}</style>

      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} data-id="element-69" data-mp="srr24zi6x5fs" />

      <div className="exit-pop relative w-full max-w-[480px] overflow-hidden rounded-2xl bg-[#0f172a] border border-[#1e293b] shadow-[0_30px_80px_rgba(0,0,0,0.6)] text-center" data-id="element-70" data-mp="xqu0y27kuffb">
        <button type="button" onClick={() => setOpen(false)} aria-label="Cerrar" className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-white/50 hover:bg-white/10 hover:text-white transition-colors" data-id="element-71" data-mp="nwbr0fbkhnn2">
          <XIcon className="h-5 w-5" data-id="element-72" data-mp="249a2mk4dyuan" />
        </button>

        <div className="px-6 sm:px-9 py-9" data-id="element-73" data-mp="249s5w0pgu8s2">
          <span className="inline-block rounded-full bg-[#36D9F4]/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[1.5px] text-[#36D9F4] mb-4" data-id="element-74" data-mp="297to6jf2i42h">
            Espera un momento
          </span>

          <h2 className="font-['Anton'] uppercase tracking-[0.5px] text-[clamp(24px,6vw,34px)] leading-[1.05] text-white mb-3" data-id="element-75" data-mp="2ed79wne3uzd3">
            ¿Te vas sin tu acceso a{' '}
            <span className="text-[#36D9F4]" data-id="element-76" data-mp="2s6gfecyxn0qc">{priceLabel}</span>?
          </h2>

          <p className="text-[14px] sm:text-[15px] leading-relaxed text-white/70 mb-6" data-id="element-77" data-mp="2nc2c7oe13pb4">
            Antes de irte: este es el mismo sistema con el que otros generaron
            su primer ingreso con AI en 48 horas — hoy a un precio que no
            volverás a ver. Asegura tu lugar antes de que termine.
          </p>

          <div className="flex items-center justify-center gap-3 mb-5 flex-wrap" data-id="element-78" data-mp="3b0vfy70w71ga">
            <span className="font-['Anton'] text-[28px] text-white/50 line-through decoration-[#94a3b8]" data-id="element-79" data-mp="372e45a6j12sj">
              $9,370
            </span>
            <span className="font-['Anton'] text-[40px] leading-none bg-gradient-to-b from-[#FD0200] to-[#AA0E13] text-white px-5 pt-1 pb-0.5 rounded-md" data-id="element-80" data-mp="1co3gv65kygov">
              {priceLabel}
            </span>
          </div>

          {/* Exit-only incentive: extra discount + copyable coupon code */}
          {showDiscount && <div className="mb-6 rounded-xl border border-dashed border-[#36D9F4]/50 bg-[#36D9F4]/10 px-4 py-4" data-id="element-81" data-mp="3lq9kl9tri89q">
              <div className="flex items-center justify-center gap-2 text-[#36D9F4]" data-id="element-82" data-mp="3qqdsibgltuoc">
                <TagIcon className="h-4 w-4 shrink-0" data-id="element-83" data-mp="3urza2a432x4v" />
                <span className="font-['Anton'] uppercase tracking-[0.5px] text-[18px] sm:text-[20px] leading-none" data-id="element-84" data-mp="3upd4gg0r6rpa">
                  {discountLabel} extra al pagar
                </span>
              </div>
              <p className="mt-2 text-[12.5px] text-white/60" data-id="element-85" data-mp="dapvmn04jc0h">
                Usa este código en el checkout:
              </p>
              <button type="button" onClick={copyCode} aria-label={`Copiar código ${couponCode}`} className="mt-2 inline-flex items-center gap-2 rounded-lg border border-[#36D9F4]/40 bg-[#0f172a] px-4 py-2 transition-colors hover:border-[#36D9F4]" data-id="element-86" data-mp="ialr1xur9v4f">
                <span className="font-mono font-bold tracking-[2px] text-[18px] text-white" data-id="element-87" data-mp="26rc3ud6u7rsi">
                  {couponCode}
                </span>
                {copied ? <CheckIcon className="h-4 w-4 text-[#3FAC00]" data-id="element-88" data-mp="1xtte4vi90paa" /> : <CopyIcon className="h-4 w-4 text-white/50" data-id="element-89" data-mp="1xul8y440uizi" />}
              </button>
              {copied && <p className="mt-1.5 text-[11.5px] font-semibold text-[#3FAC00]" data-id="element-90" data-mp="1sw08q4orrzb2">
                  ¡Código copiado!
                </p>}
            </div>}

          <button type="button" onClick={() => {
          setOpen(false);
          onAccept();
        }} className="relative overflow-hidden flex items-center justify-center gap-2.5 w-full bg-[#3FAC00] hover:bg-[#43B800] text-white font-extrabold text-[18px] tracking-tight p-4 rounded-lg transition-all duration-150 hover:-translate-y-0.5 active:translate-y-[2px] shadow-[0_4px_0_#2C7A00,0_1px_3px_rgba(0,0,0,0.2)] active:shadow-[0_2px_0_#2C7A00]" data-id="element-91" data-mp="2ljdztm5se0n9">
            <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-lg bg-gradient-to-b from-white/35 to-transparent" data-id="element-92" data-mp="2ctm5ba5a19ry" />
            <ShoppingCartIcon className="relative w-5 h-5 drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" data-id="element-93" data-mp="2hldx1o065ljo" />
            <span className="relative [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]" data-id="element-94" data-mp="23tz1khp2rrvq">
              Sí, quiero mi acceso ahora
            </span>
          </button>

          <button type="button" onClick={() => setOpen(false)} className="mt-4 text-[12.5px] text-white/40 hover:text-white/70 underline underline-offset-2 transition-colors" data-id="element-95" data-mp="2vk3td1f5zh6z">
            No gracias, prefiero perder esta oferta
          </button>
        </div>
      </div>
    </div>;
}